import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';

// ─── Shared UI ──────────────────────────────────────────────────
const BackgroundBlobs = () => (
    <div className="fixed inset-0 overflow-hidden pointer-events-none z-[-1]">
        <div className="absolute top-[-5%] right-[-10%] w-[300px] sm:w-[400px] h-[300px] sm:h-[400px] rounded-full bg-orange-400 opacity-20 blur-3xl" />
        <div className="absolute top-[50%] left-[-15%] w-[300px] sm:w-[400px] h-[300px] sm:h-[400px] rounded-full bg-red-400 opacity-10 blur-3xl transform -translate-y-1/2" />
    </div>
);

const EyeOpen = () => (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
    </svg>
);

const EyeClosed = () => (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
    </svg>
);

// Password rule indicator
const RuleItem = ({ valid, empty, text }) => (
    <li className={`flex items-center gap-2 text-xs transition-colors duration-200 ${empty ? 'text-slate-500' : valid ? 'text-emerald-600 font-bold' : 'text-red-500 font-bold'}`}>
        {empty ? (
            <svg className="w-3.5 h-3.5 text-slate-300" fill="currentColor" viewBox="0 0 24 24"><circle cx="12" cy="12" r="4" /></svg>
        ) : valid ? (
            <svg className="w-3.5 h-3.5 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
        ) : (
            <svg className="w-3.5 h-3.5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
        )}
        <span>{text}</span>
    </li>
);

// ─── Main Component ─────────────────────────────────────────────
const ResetPasswordPage = () => {
    const { uidb64, token } = useParams();
    const navigate = useNavigate();

    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showNew, setShowNew] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [loading, setLoading] = useState(false);
    const [submitError, setSubmitError] = useState(false);

    // 'form' | 'success' | 'fail'
    const [view, setView] = useState('form');

    // Password validation state
    const [isLengthValid, setIsLengthValid] = useState(false);
    const [isMixedValid, setIsMixedValid] = useState(false);
    const [isComplexValid, setIsComplexValid] = useState(false);
    const [isMatch, setIsMatch] = useState(false);
    const [passwordEmpty, setPasswordEmpty] = useState(true);
    const [confirmTouched, setConfirmTouched] = useState(false);

    const badSequences = [
        "01234567890123456789", "98765432109876543210",
        "abcdefghijklmnopqrstuvwxyz", "zyxwvutsrqponmlkjihgfedcba",
        "qwertyuiop", "asdfghjkl", "zxcvbnm"
    ];

    const validatePassword = useCallback((val) => {
        if (val.length === 0) {
            setPasswordEmpty(true);
            setIsLengthValid(false); setIsMixedValid(false); setIsComplexValid(false);
            return;
        }
        setPasswordEmpty(false);
        setIsLengthValid(val.length >= 8);
        setIsMixedValid(/[a-zA-Z]/.test(val) && /[0-9]/.test(val));

        const hasRepeating = /(.)\1{4,}/.test(val);
        const lowerVal = val.toLowerCase();
        let isSequence = false;
        if (val.length >= 5) {
            for (const seq of badSequences) {
                if (seq.includes(lowerVal)) { isSequence = true; break; }
            }
        }
        setIsComplexValid(!hasRepeating && !isSequence);
    }, []);

    useEffect(() => {
        validatePassword(newPassword);
    }, [newPassword, validatePassword]);

    useEffect(() => {
        if (confirmPassword.length > 0 || confirmTouched) {
            setIsMatch(newPassword === confirmPassword && newPassword.length > 0);
        }
    }, [newPassword, confirmPassword, confirmTouched]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        const isFormValid = isLengthValid && isMixedValid && isComplexValid && isMatch;
        if (!isFormValid) {
            setSubmitError(true);
            setConfirmTouched(true);
            return;
        }
        setLoading(true);
        setSubmitError(false);

        try {
            await axios.post(`/accounts/reset-password/${uidb64}/${token}/`, { password: newPassword });
            setView('success');
        } catch (err) {
            setView('fail');
        }
        setLoading(false);
    };

    return (
        <div dir="rtl" className="bg-background text-slate-800 font-sans overflow-x-hidden max-w-[100vw] selection:bg-primary selection:text-white relative min-h-screen flex flex-col">
            <BackgroundBlobs />

            {/* Nav */}
            <nav className="max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 flex justify-between items-center relative z-10">
                <div>
                    <div className="flex items-center select-none" dir="ltr">
                        <span className="font-logo font-light text-2xl sm:text-3xl text-slate-800 tracking-wide">BrokersConnect</span>
                    </div>
                </div>
                <div className="flex items-center">
                    <Link to="/login" className="px-5 py-2 text-sm sm:text-base rounded-full border-2 border-slate-200 text-slate-700 font-bold hover:border-primary hover:text-primary transition-all bg-white/50 backdrop-blur-sm">
                        {view === 'form' ? 'ביטול וחזרה להתחברות' : 'חזרה להתחברות'}
                    </Link>
                </div>
            </nav>

            <div className="flex-1 flex flex-col justify-center items-center px-4 w-full relative z-10 pb-12 sm:pb-20">
                <div className="w-full max-w-md relative">
                    <div className="absolute inset-0 bg-primary opacity-5 transform translate-y-3 translate-x-3 rounded-3xl" />

                    {/* ─── Form View ─── */}
                    {view === 'form' && (
                        <form onSubmit={handleSubmit} className="relative bg-surface p-8 sm:p-10 rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.08)] border border-slate-100">
                            <div className="text-center mb-6">
                                <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-orange-50 mb-4">
                                    <svg className="w-7 h-7 text-primary" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                    </svg>
                                </div>
                                <h2 className="text-2xl sm:text-3xl font-bold text-slate-800">יצירת סיסמה חדשה</h2>
                            </div>

                            <div className="space-y-5">
                                {/* New Password */}
                                <div>
                                    <label className="block text-slate-600 text-xs sm:text-sm font-bold mb-1.5">סיסמה חדשה</label>
                                    <div className="relative">
                                        <input type={showNew ? 'text' : 'password'} value={newPassword}
                                            onChange={(e) => { setNewPassword(e.target.value); setSubmitError(false); }}
                                            required placeholder="••••••••"
                                            className="w-full bg-slate-50 border border-slate-200 rounded-lg py-3 pl-4 pr-11 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all" dir="ltr" />
                                        <button type="button" onClick={() => setShowNew(!showNew)}
                                            className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-slate-400 hover:text-primary transition-colors focus:outline-none">
                                            {showNew ? <EyeClosed /> : <EyeOpen />}
                                        </button>
                                    </div>

                                    {/* Password strength guide */}
                                    <div className="mt-3 bg-slate-50 border border-slate-100 rounded-lg p-3">
                                        <p className="text-[11px] font-bold text-slate-600 mb-2 uppercase tracking-wide">דרישות חובה לסיסמה:</p>
                                        <ul className="space-y-2 px-0.5">
                                            <RuleItem valid={isLengthValid} empty={passwordEmpty} text="לפחות 8 תווים" />
                                            <RuleItem valid={isMixedValid} empty={passwordEmpty} text="אותיות באנגלית (A-Z) ומספרים (0-9)" />
                                            <RuleItem valid={isComplexValid} empty={passwordEmpty} text="ללא סיסמאות פשוטות מדי (כגון 12345678 או 00000)" />
                                        </ul>
                                    </div>
                                </div>

                                {/* Confirm Password */}
                                <div>
                                    <label className="block text-slate-600 text-xs sm:text-sm font-bold mb-1.5">הקלד שוב לאימות</label>
                                    <div className="relative">
                                        <input type={showConfirm ? 'text' : 'password'} value={confirmPassword}
                                            onChange={(e) => { setConfirmPassword(e.target.value); setConfirmTouched(true); setSubmitError(false); }}
                                            required placeholder="••••••••"
                                            className="w-full bg-slate-50 border border-slate-200 rounded-lg py-3 pl-4 pr-11 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all" dir="ltr" />
                                        <button type="button" onClick={() => setShowConfirm(!showConfirm)}
                                            className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-slate-400 hover:text-primary transition-colors focus:outline-none">
                                            {showConfirm ? <EyeClosed /> : <EyeOpen />}
                                        </button>
                                    </div>

                                    {/* Match indicator */}
                                    {(confirmTouched || confirmPassword.length > 0) && (
                                        <div className={`mt-2 text-xs flex items-center gap-1.5 font-semibold px-1 ${isMatch ? 'text-emerald-600' : 'text-red-500'}`}>
                                            {isMatch ? (
                                                <><svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg><span>הסיסמאות תואמות</span></>
                                            ) : (
                                                <><svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg><span>הסיסמאות אינן תואמות</span></>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {submitError && (
                                <div className="mt-6 bg-red-50 border border-red-200 text-red-600 text-sm font-bold rounded-lg p-3 text-center">
                                    אנא תקן את השגיאות המסומנות באדום כדי להמשיך.
                                </div>
                            )}

                            <button type="submit" disabled={loading}
                                className="mt-6 w-full bg-gradient-to-l from-primary to-amber-500 hover:from-primaryDark hover:to-primary text-white font-bold text-base sm:text-lg py-3.5 rounded-xl shadow-lg hover:shadow-primary/30 transform transition-all hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-60">
                                {loading ? 'שומר...' : 'שמור סיסמה והתחבר'}
                            </button>
                        </form>
                    )}

                    {/* ─── Success View ─── */}
                    {view === 'success' && (
                        <div className="relative bg-surface p-8 sm:p-12 rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.08)] border border-slate-100 text-center">
                            <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-emerald-50 ring-4 ring-emerald-50/50 mb-6 relative">
                                <svg className="w-8 h-8 sm:w-10 sm:h-10 text-emerald-500" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
                                </svg>
                                <div className="absolute -bottom-1 -right-1 bg-white rounded-full p-0.5">
                                    <div className="bg-emerald-500 rounded-full p-1">
                                        <svg className="w-3 h-3 sm:w-4 sm:h-4 text-white" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                        </svg>
                                    </div>
                                </div>
                            </div>
                            <h2 className="text-2xl sm:text-3xl font-bold text-slate-800 mb-3">הסיסמה שונתה בהצלחה!</h2>
                            <p className="text-base text-slate-500 leading-relaxed mb-8">
                                הסיסמה לחשבונך עודכנה ונשמרה במערכת באופן מאובטח.<br />
                                מעתה תוכל להתחבר באמצעות הסיסמה החדשה.
                            </p>
                            <Link to="/login"
                                className="inline-block w-full bg-gradient-to-l from-primary to-amber-500 hover:from-primaryDark hover:to-primary text-white font-bold text-base sm:text-lg py-3.5 rounded-xl shadow-lg hover:shadow-primary/30 transform transition-all hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary">
                                המשך להתחברות
                            </Link>
                        </div>
                    )}

                    {/* ─── Fail View ─── */}
                    {view === 'fail' && (
                        <div className="relative bg-surface p-8 sm:p-12 rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.08)] border border-slate-100 text-center">
                            <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-red-50 ring-4 ring-red-50/50 mb-6">
                                <svg className="w-8 h-8 sm:w-10 sm:h-10 text-red-500" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                </svg>
                            </div>
                            <h2 className="text-2xl sm:text-3xl font-bold text-slate-800 mb-3">הקישור פג תוקף</h2>
                            <p className="text-base text-slate-500 leading-relaxed mb-8">
                                הקישור לאיפוס הסיסמה אליו ניסית לגשת אינו תקין, או שכבר נעשה בו שימוש.<br />
                                <span className="text-sm opacity-80 mt-2 block">מטעמי אבטחה, קישורים לאיפוס סיסמה תקפים לזמן מוגבל בלבד.</span>
                            </p>
                            <Link to="/login"
                                className="inline-block w-full bg-gradient-to-l from-primary to-amber-500 hover:from-primaryDark hover:to-primary text-white font-bold text-base sm:text-lg py-3.5 rounded-xl shadow-lg hover:shadow-primary/30 transform transition-all hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary">
                                בקש קישור חדש
                            </Link>
                            <div className="mt-6 pt-5 border-t border-slate-100">
                                <Link to="/login" className="text-sm font-semibold text-slate-500 hover:text-primary transition-colors">
                                    חזרה למסך ההתחברות
                                </Link>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ResetPasswordPage;
